import { Button, SelectInput, SelectInputItem, TextField, useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SerieVideoLangViewModel } from '@/shared/api/generated';
import { Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { useAvailableSerieVideoLanguages } from '../../../hooks/useAvailableSerieVideoLanguages';
import { useSaveSerieVideoLang } from '../../../hooks/useSaveSerieVideoLang';
import {
  type SerieVideoLangFormValues,
  serieVideoLangFormSchema,
} from '../../../types/serieVideoLangForm';

export interface SerieVideoLangFormProps {
  readonly videoId: number;
  /** Idioma em edição; `null` para criação. */
  readonly lang: SerieVideoLangViewModel | null;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

const DEFAULT_VALUES: SerieVideoLangFormValues = {
  cultureInfo: '',
  title: '',
  description: '',
  channel: '',
};

/**
 * Formulário de Idioma do Vídeo (criar/editar), usado em tela dedicada (não
 * modal). No modo criação, **Idioma** é um `SelectInput` alimentado por
 * `useAvailableSerieVideoLanguages`; na edição, o campo é texto fixo. Fiel aos
 * frames *Idioma do Vídeo · Criar/Editar* de `design/series.pen`, incluindo os
 * botões soltos abaixo do card.
 */
export function SerieVideoLangForm({ videoId, lang, onSaved, onCancel }: SerieVideoLangFormProps) {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const { save, isPending } = useSaveSerieVideoLang(videoId);
  const { options: availableLanguages } = useAvailableSerieVideoLanguages(videoId);

  const isEdit = lang?.id != null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SerieVideoLangFormValues>({
    resolver: zodResolver(serieVideoLangFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(
      lang
        ? {
            cultureInfo: lang.cultureInfo ?? '',
            title: lang.title ?? '',
            description: lang.resumee ?? '',
            channel: lang.embed ?? '',
          }
        : DEFAULT_VALUES,
    );
  }, [lang, reset]);

  const onSubmit = handleSubmit(values => {
    save(values, lang?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'videoLangs.toast.updateSuccess' : 'videoLangs.toast.createSuccess'),
        });
        onSaved();
      },
      onError: () => alert({ alertType: 'warning', title: t('videoLangs.toast.saveError') }),
    });
  });

  return (
    <form className="flex flex-col gap-xs" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
        <h2 className="ts-heading-sm text-ctx-content-title">
          {t(isEdit ? 'videoLangs.form.editTitle' : 'videoLangs.form.createTitle')}
        </h2>

        {isEdit ? (
          <div className="flex flex-col gap-quark">
            <span className="ts-subtitle-xxs text-ctx-content-title">
              {t('videoLangs.form.language')}
            </span>
            <span className="ts-paragraph-xxs text-ctx-content-title">{lang?.cultureInfo}</span>
          </div>
        ) : (
          <Controller
            control={control}
            name="cultureInfo"
            render={({ field }) => (
              <SelectInput
                label={`${t('videoLangs.form.language')}*`}
                value={field.value}
                onValueChange={field.onChange}
                size="md"
                hasError={Boolean(errors.cultureInfo)}
                {...(errors.cultureInfo
                  ? { helper: t('videoLangs.form.errors.languageRequired') }
                  : {})}
              >
                {availableLanguages.map(option => (
                  <SelectInputItem
                    key={option.key}
                    value={option.key ?? ''}
                    custom={{ root: 'text-nowrap' }}
                  >
                    {option.value}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
        )}

        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <TextField
              label={`${t('videoLangs.form.title')}*`}
              placeholder={t('videoLangs.form.titlePlaceholder')}
              value={field.value}
              onChange={e => field.onChange(e.target.value)}
              size="md"
              fullWidth
              hasError={Boolean(errors.title)}
              {...(errors.title ? { helper: t('videoLangs.form.errors.titleRequired') } : {})}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <FormField label={t('videoLangs.form.description')}>
              <Textarea
                placeholder={t('videoLangs.form.descriptionPlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                rows={3}
              />
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="channel"
          render={({ field }) => (
            <TextField
              label={`${t('videoLangs.form.channel')}*`}
              placeholder={t('videoLangs.form.channelPlaceholder')}
              value={field.value}
              onChange={e => field.onChange(e.target.value)}
              size="md"
              fullWidth
              hasError={Boolean(errors.channel)}
              {...(errors.channel ? { helper: t('videoLangs.form.errors.channelRequired') } : {})}
            />
          )}
        />
      </div>

      <div className="flex items-center justify-end gap-nano">
        <Button hierarchy="secondary" size="md" type="button" onClick={onCancel}>
          {t('videoLangs.form.cancel')}
        </Button>
        <Button
          hierarchy="primary"
          size="md"
          type="submit"
          isLoading={isPending}
          disabled={isPending}
        >
          {t('videoLangs.form.save')}
        </Button>
      </div>
    </form>
  );
}
